/**
 * 플랫폼 사용자 목록 화면.
 * 레거시 `ruda-admin-web/pages/admin/usersInfo.js` 포팅.
 * 검색 필터·테이블 컬럼/상태 태그를 레거시와 동일하게 맞춘다.
 */
import { BUSINESS_OWNER_TYPE } from '@/entities/user/api/user.api';
import { usePlatformUserList } from '@/entities/user/hooks/usePlatformUsers';
import {
  formatDateTime,
  formatPhoneNumber,
  getUserRowId,
} from '@/pages/users/userListUtils';
import {
  AccountStatusTag,
  BusinessOwnerTypeTag,
  BusinessVerificationStatusTag,
  ContractStatusTag,
  FinancialVerificationStatusTag,
  PromoLicenseStatusTag,
  SalesApprovalStatusTag,
} from '@/pages/users/UserStatusTags';
import { UserBulkRegisterDialog } from '@/pages/users/UserBulkRegisterDialog';
import { downloadUserListExcel } from '@/pages/users/userExportUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow, Stack } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { userDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Download, Upload, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

/** 레거시 toStatusQueryValue — API 쿼리용 underscore 제거 */
const toPromoLicenseQueryValue = (value: string) => value.replace(/_/g, '');

const PROMO_LICENSE_STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'NOT_REGISTERED', label: '미등록' },
  { value: 'PENDING', label: '확인 전' },
  { value: 'APPROVED', label: '승인' },
  { value: 'REJECTED', label: '승인불가' },
] as const;

const SALES_APPROVAL_STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'TRANSACTION_POSSIBLE', label: '거래 신청 가능' },
  { value: 'SETTLEMENT_POSSIBLE', label: '정산 가능' },
] as const;

const BUSINESS_OWNER_TYPE_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: BUSINESS_OWNER_TYPE.INDIVIDUAL, label: '개인' },
  { value: BUSINESS_OWNER_TYPE.CORPORATE, label: '법인' },
] as const;

const USER_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  email: '',
  phoneNumber: '',
  companyName: '',
  promoLicenseStatus: '',
  salesApprovalStatus: '',
  businessOwnerType: '',
  beforeBusinessRegistrationApproval: false,
  completedBankAccountVerification: false,
  suspendedUsers: false,
};

export const UsersPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(USER_SEARCH_DEFAULTS);

  const [emailInput, setEmailInput] = useState(values.email);
  const [phoneInput, setPhoneInput] = useState(values.phoneNumber);
  const [companyNameInput, setCompanyNameInput] = useState(values.companyName);
  const [bulkRegisterOpen, setBulkRegisterOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setEmailInput(values.email);
    setPhoneInput(values.phoneNumber);
    setCompanyNameInput(values.companyName);
  }, [values.email, values.phoneNumber, values.companyName]);

  const search = useMemo(
    () => ({
      email: values.email || undefined,
      phoneNumber: values.phoneNumber || undefined,
      companyName: values.companyName || undefined,
      isBusinessVerified: values.beforeBusinessRegistrationApproval
        ? true
        : undefined,
      isFinancialVerified: values.completedBankAccountVerification
        ? true
        : undefined,
      // 레거시: 이용 정지만 보기 → isActive=false
      isActive: values.suspendedUsers ? false : undefined,
      promoLicenseStatus: values.promoLicenseStatus
        ? toPromoLicenseQueryValue(values.promoLicenseStatus)
        : undefined,
      salesApprovalStatus: values.salesApprovalStatus || undefined,
      businessOwnerType:
        values.businessOwnerType === BUSINESS_OWNER_TYPE.INDIVIDUAL ||
        values.businessOwnerType === BUSINESS_OWNER_TYPE.CORPORATE
          ? values.businessOwnerType
          : undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = usePlatformUserList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const users = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      email: emailInput.trim().toLowerCase(),
      phoneNumber: phoneInput.trim(),
      companyName: companyNameInput.trim(),
      page: 1,
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const count = await downloadUserListExcel(search);
      toast.success(
        `사용자 목록을 다운로드했습니다. (${count.toLocaleString()}건)`,
      );
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Users}
        title="사용자 정보 관리"
        description="플랫폼 사용자 조회 및 관리"
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || isLoading || totalCount === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
            </Button>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                onClick={() => setBulkRegisterOpen(true)}
              >
                <Upload className="mr-2 h-4 w-4" />
                엑셀 일괄 등록
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <FilterRow as="div" gap={12}>
            <div className="space-y-1">
              <Label htmlFor="user-email">사용자 이메일</Label>
              <Input
                id="user-email"
                placeholder="사용자 이메일"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-48"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-phone">전화번호</Label>
              <Input
                id="user-phone"
                placeholder="전화번호"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-company">업체명</Label>
              <Input
                id="user-company"
                placeholder="업체명"
                value={companyNameInput}
                onChange={(e) => setCompanyNameInput(e.target.value)}
                className="w-40"
              />
            </div>
            <Button type="submit" className="ruda-button mt-6">
              검색
            </Button>
          </FilterRow>

          <FilterRow as="div" gap={12} className="items-end">
            <div className="space-y-1">
              <Label>판촉 영업자 신고증 상태</Label>
              <Select
                value={values.promoLicenseStatus || 'all'}
                onValueChange={(value) =>
                  setValues({
                    promoLicenseStatus: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="판촉 영업자 신고증 상태" />
                </SelectTrigger>
                <SelectContent>
                  {PROMO_LICENSE_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>영업 승인 상태</Label>
              <Select
                value={values.salesApprovalStatus || 'all'}
                onValueChange={(value) =>
                  setValues({
                    salesApprovalStatus: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="영업 승인 상태" />
                </SelectTrigger>
                <SelectContent>
                  {SALES_APPROVAL_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-business-owner-type">사업자 유형</Label>
              <Select
                value={values.businessOwnerType || 'all'}
                onValueChange={(value) =>
                  setValues({
                    businessOwnerType: value === 'all' ? '' : value,
                    page: 1,
                  })
                }
              >
                <SelectTrigger
                  id="user-business-owner-type"
                  className="w-48"
                  aria-label="사업자 유형"
                >
                  <SelectValue placeholder="사업자 유형" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_OWNER_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FilterRow>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.beforeBusinessRegistrationApproval}
                onCheckedChange={(v) =>
                  setValues({
                    beforeBusinessRegistrationApproval: v === true,
                    page: 1,
                  })
                }
              />
              사업자 인증 확인
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.completedBankAccountVerification}
                onCheckedChange={(v) =>
                  setValues({
                    completedBankAccountVerification: v === true,
                    page: 1,
                  })
                }
              />
              계좌인증 여부
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={values.suspendedUsers}
                onCheckedChange={(v) =>
                  setValues({
                    suspendedUsers: v === true,
                    page: 1,
                  })
                }
              />
              이용 정지 사용자만 보기
            </label>
          </div>
        </Stack>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="사용자 목록을 불러오는 중..." />
          </div>
        ) : users.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 사용자가 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>유저 ID</TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>전화번호</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>사업자 유형</TableHead>
                  <TableHead>가입 일자</TableHead>
                  <TableHead>판촉영업자신고</TableHead>
                  <TableHead>사업자등록증</TableHead>
                  <TableHead>계좌 인증</TableHead>
                  <TableHead>영업 인증</TableHead>
                  <TableHead>위탁계약</TableHead>
                  <TableHead>계정</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* 같은 이메일로 탈퇴·재가입한 계정이 함께 나오므로 행 식별·상세 이동은 사용자 ID 로 한다 */}
                {users.map((user) => (
                  <TableRow
                    key={user.id ?? user.userId ?? user.email}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      const rowUserId = user.id ?? user.userId;
                      if (rowUserId == null) return;
                      navigate({
                        to: userDetailPath(rowUserId),
                      } as never);
                    }}
                  >
                    <TableCell>{getUserRowId(user)}</TableCell>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{formatPhoneNumber(user.phoneNumber)}</TableCell>
                    <TableCell>{user.companyName || '-'}</TableCell>
                    <TableCell>
                      <BusinessOwnerTypeTag type={user.businessOwnerType} />
                    </TableCell>
                    <TableCell>{formatDateTime(user.registeredAt)}</TableCell>
                    <TableCell>
                      <PromoLicenseStatusTag status={user.promoLicenseStatus} />
                    </TableCell>
                    <TableCell>
                      <BusinessVerificationStatusTag
                        status={user.businessVerificationStatus}
                      />
                    </TableCell>
                    <TableCell>
                      <FinancialVerificationStatusTag
                        status={user.financialVerificationStatus}
                      />
                    </TableCell>
                    <TableCell>
                      <SalesApprovalStatusTag
                        status={user.salesApprovalStatus}
                      />
                    </TableCell>
                    <TableCell>
                      <ContractStatusTag record={user} />
                    </TableCell>
                    <TableCell>
                      <AccountStatusTag record={user} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <UserBulkRegisterDialog
        open={bulkRegisterOpen}
        onOpenChange={setBulkRegisterOpen}
      />
    </div>
  );
};
